import { COLOR_PALETTE } from "../../excalidraw/colors";

import type { MindmapTree } from "./mindmapTree";

/**
 * Branch colouring for the auto-mindmap.
 *
 * Each level-1 branch is assigned one colour from the app's canonical element
 * palette, cycling by branch index, and that colour is inherited by the whole
 * subtree beneath it. The root stays neutral. The emitter reads the resulting
 * blockId → colour map to paint node text/stroke and connector strokes, so a
 * generated map reads like the classic radial mindmap (one colour per branch)
 * while using only the app's own colours.
 */

/** Shade index into a palette colour tuple; matches the element stroke default. */
const BRANCH_SHADE = 4;

/**
 * Ordered branch palette, drawn from `COLOR_PALETTE` (the canonical element
 * colours) — deliberately not hardcoded hexes. Order mirrors the app's own
 * colour rows so adjacent branches stay visually distinct.
 */
export const MINDMAP_BRANCH_COLORS: readonly string[] = [
  COLOR_PALETTE.blue[BRANCH_SHADE],
  COLOR_PALETTE.green[BRANCH_SHADE],
  COLOR_PALETTE.orange[BRANCH_SHADE],
  COLOR_PALETTE.grape[BRANCH_SHADE],
  COLOR_PALETTE.teal[BRANCH_SHADE],
  COLOR_PALETTE.red[BRANCH_SHADE],
  COLOR_PALETTE.violet[BRANCH_SHADE],
  COLOR_PALETTE.cyan[BRANCH_SHADE],
  COLOR_PALETTE.yellow[BRANCH_SHADE],
  COLOR_PALETTE.pink[BRANCH_SHADE],
];

/** The root node's colour — neutral, so branches read as the colourful part. */
export const MINDMAP_NEUTRAL_COLOR: string = COLOR_PALETTE.black;

/**
 * The colour names agents may use. Every name resolves to the same
 * shade the positional branch palette uses, so an agent's "blue" and the
 * auto-assigned blue read back identically. `purple` is the friendly name for
 * open-color's `violet`; `gray` is the neutral choice, distinct from the
 * root's black.
 */
export const MINDMAP_COLOR_NAMES: readonly string[] = [
  "gray",
  "blue",
  "green",
  "orange",
  "red",
  "purple",
  "yellow",
  "pink",
  "teal",
  "cyan",
  "grape",
  "violet",
];

/** The `COLOR_PALETTE` row each agent name draws from (`purple` = `violet`). */
const ROW_BY_NAME: Record<string, keyof typeof COLOR_PALETTE> = {
  gray: "gray",
  blue: "blue",
  green: "green",
  orange: "orange",
  red: "red",
  purple: "violet",
  yellow: "yellow",
  pink: "pink",
  teal: "teal",
  cyan: "cyan",
  grape: "grape",
  violet: "violet",
};

const shadeOf = (name: string, shade: number): string | undefined => {
  const row = Object.hasOwn(ROW_BY_NAME, name.toLowerCase())
    ? COLOR_PALETTE[ROW_BY_NAME[name.toLowerCase()]]
    : undefined;
  return Array.isArray(row) ? row[shade] : undefined;
};

/** Light shade for a note's fill: legible under black text. */
const TINT_SHADE = 1;

/** An unknown colour name; the message lists the palette. */
export class MindmapColorError extends Error {}

/** The branch hex for a palette name, case-insensitive. */
export const resolveMindmapColor = (name: string): string => {
  const hex = shadeOf(name, BRANCH_SHADE);
  if (!hex) {
    throw new MindmapColorError(
      `Unknown color "${name}". Use one of: ${MINDMAP_COLOR_NAMES.join(", ")}.`,
    );
  }
  return hex;
};

/** The light fill of the same row as `resolveMindmapColor(name)` (notes). */
export const resolveMindmapTint = (name: string): string => {
  const hex = shadeOf(name, TINT_SHADE);
  if (!hex) {
    throw new MindmapColorError(
      `Unknown color "${name}". Use one of: ${MINDMAP_COLOR_NAMES.join(", ")}.`,
    );
  }
  return hex;
};

/** The faintest fill of the row: a container's wash, light under its members. */
export const resolveMindmapWash = (name: string): string => {
  const hex = shadeOf(name, 0);
  if (!hex) {
    throw new MindmapColorError(
      `Unknown color "${name}". Use one of: ${MINDMAP_COLOR_NAMES.join(", ")}.`,
    );
  }
  return hex;
};

/**
 * The agent name for a branch/stroke hex, so a read prints `color=purple`
 * for what the agent asked for as `purple`. Two names may alias one hex
 * (`purple`/`violet`); the first-declared name in `MINDMAP_COLOR_NAMES` wins.
 * Null for a hex outside the agent palette (the neutral root included).
 */
export const mindmapColorName = (hex: string): string | null => {
  const wanted = hex.toLowerCase();
  for (const name of MINDMAP_COLOR_NAMES) {
    if (shadeOf(name, BRANCH_SHADE)?.toLowerCase() === wanted) {
      return name;
    }
  }
  return null;
};

/** Trust a saved explicit name only while it still matches the element colour. */
export const matchingMindmapColorName = (
  name: unknown,
  hex: unknown,
): string | null => {
  if (typeof name !== "string" || typeof hex !== "string") {
    return null;
  }
  const normalized = name.toLowerCase();
  return shadeOf(normalized, BRANCH_SHADE)?.toLowerCase() === hex.toLowerCase()
    ? normalized
    : null;
};

// A node's own colour beats the one it would inherit, for its whole subtree.
const paint = (
  node: MindmapTree,
  color: string,
  out: Map<string, string>,
): void => {
  const own = node.color ?? color;
  out.set(node.blockId, own);
  for (const child of node.children) {
    paint(child, own, out);
  }
};

/**
 * Map every blockId in the tree to its colour.
 *
 * Colouring starts at the first level that *actually branches* (spec: a
 * single-branch/linear document must not be painted one branch colour). Walking
 * down from the root, every node on a single-child spine stays neutral; the
 * first node with two or more children is the branch point, and each of its
 * children owns a palette colour inherited by that child's whole subtree. A
 * purely linear document (no node ever branches) stays entirely neutral.
 */
export const assignBranchColors = (tree: MindmapTree): Map<string, string> => {
  const colors = new Map<string, string>();

  // Descend the single-child spine, keeping it neutral, until a node branches
  // (>=2 children) or the chain ends (leaf, or a lone child that is itself a
  // spine link). `branchPoint` is where colours begin.
  // A spine node that asked for a colour gets it (agents may colour the
  // root); the spine's neutrality is the default, not a rule.
  let branchPoint = tree;
  colors.set(branchPoint.blockId, branchPoint.color ?? MINDMAP_NEUTRAL_COLOR);
  while (branchPoint.children.length === 1) {
    branchPoint = branchPoint.children[0];
    colors.set(branchPoint.blockId, branchPoint.color ?? MINDMAP_NEUTRAL_COLOR);
  }

  // At a real branch point (>=2 children) each child seeds a coloured subtree;
  // at a leaf this loop is empty, leaving the linear document neutral.
  branchPoint.children.forEach((child, index) => {
    const color = MINDMAP_BRANCH_COLORS[index % MINDMAP_BRANCH_COLORS.length];
    paint(child, color, colors);
  });

  return colors;
};
